/**
 * EnterpriseRoute — wraps enterprise pages.
 * - Redirects unauthenticated users to /login.
 * - Redirects non-enterprise users to /app.
 * - Redirects enterprise users who haven't completed onboarding to /enterprise/onboarding.
 */
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useBilling } from '../../../context/BillingContext';
import { fetchEnterpriseProfile } from '../../../api/enterpriseApi';
import { cached } from '../../../api/apiCache';

interface Props {
  children: React.ReactNode;
  requireOnboarding?: boolean; // false = skip onboarding check (used on the onboarding page itself)
}

export const EnterpriseRoute: React.FC<Props> = ({ children, requireOnboarding = true }) => {
  const { user, loading: authLoading } = useAuth();
  const { subscription, loading: billingLoading } = useBilling();
  const location = useLocation();

  // 'unknown' = not yet checked, true/false = result
  const [onboardingDone, setOnboardingDone] = useState<boolean | 'unknown'>('unknown');
  const [profileLoading, setProfileLoading] = useState(true);

  const isEnterprise = subscription?.plan_tier === 'enterprise';

  useEffect(() => {
    // Skip profile check if billing is still loading, not enterprise, or onboarding check disabled
    if (billingLoading || !isEnterprise || !requireOnboarding) {
      setProfileLoading(false);
      return;
    }

    setProfileLoading(true);
    cached('enterprise-profile', 120_000, fetchEnterpriseProfile)
      .then(p => setOnboardingDone(p.onboarding_completed ? true : false))
      .catch(err => {
        // 404 = no profile yet → needs onboarding
        // Any other error (network, 5xx) → don't block, allow through to avoid wrong redirects
        const msg = (err?.message || '').toLowerCase();
        if (msg.includes('404') || msg.includes('not found')) {
          setOnboardingDone(false);
        } else {
          setOnboardingDone(true); // fail-open: don't redirect on unexpected errors
        }
      })
      .finally(() => setProfileLoading(false));
  }, [isEnterprise, requireOnboarding, billingLoading]);

  // Wait for auth + billing + profile check
  // Also wait for subscription to be defined (not undefined), same as ProtectedRoute does,
  // to avoid a brief false isEnterprise=false that causes a wrong redirect to /app.
  if (authLoading || billingLoading || typeof subscription === 'undefined' || (requireOnboarding && isEnterprise && profileLoading)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'inherit' }}>
        Loading…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isEnterprise) {
    return <Navigate to="/app" replace />;
  }

  if (requireOnboarding && onboardingDone === false && location.pathname !== '/enterprise/onboarding') {
    return <Navigate to="/enterprise/onboarding" replace />;
  }

  return <>{children}</>;
};
